// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — page-JS heartbeat responder + lease model (ABB-S3)
// ═══════════════════════════════════════════════════════════════════════════
//
// The BROWSER side of the §4.3 / SM-14 heartbeat. The companion's lease liveness
// gate keys ONLY off an APP-LEVEL `{type:pong,seq}` the PAGE JS sends in reply
// to a companion `{type:ping,seq}` (apps/node-companion/src/heartbeat.ts:9-22).
// The browser's AUTOMATIC WebSocket-protocol pong does NOT count — it is sent by
// the browser's WS implementation, not the page JS, so a wedged page would keep
// the lease alive via protocol pongs while the JS is dead. THIS module answers
// the app-level ping from page JS, synchronously, echoing `seq` verbatim.
//
// THE LEASE MODEL (documented contract, mirrors node-companion/heartbeat.ts):
//   • The companion pings every ~15s (DEFAULT_PING_INTERVAL_MS) on the active
//     seat. On a MISSED app-level pong it opens a single grace window (~20s) on
//     the NEXT tick — it does NOT re-ping while waiting — so a pong sent
//     synchronously on receipt always arrives before the next tick.
//   • ANY in-window app-level frame ALSO renews the lease on the companion side
//     (wsServer.ts:860 recordActivity) — an rpc-result / push frame keeps the
//     seat alive even between pings. The pong is the IDLE keepalive (when the
//     page is otherwise silent). This module therefore only OWNS the pong; the
//     "any frame renews" half is satisfied by the client sending those frames.
//
// LEAF module: imports ONLY the codec types. No socket, no editorStore, no
// node-companion. The transport `send` arrives via DI (a `(text)=>void` sink).
// ═══════════════════════════════════════════════════════════════════════════

import {
  type InboundPingFrame,
  type OutboundPongFrame,
  serializeFrame,
} from "./wsFrameCodec";

/** The transport send sink the responder writes the pong text to (DI). */
export type HeartbeatSend = (text: string) => void;

/**
 * The page-JS heartbeat responder. On each companion `{type:ping,seq}` it sends
 * `{type:pong,seq}` SYNCHRONOUSLY (echo `seq`) so the companion's lease is
 * renewed at the page-JS layer — the single correct liveness proof.
 */
export class HeartbeatResponder {
  private readonly send: HeartbeatSend;

  /** The last `seq` the responder answered (introspection / tests). */
  private lastAnsweredSeq: number | null = null;

  /** Count of pongs sent (introspection / tests). */
  private pongCount = 0;

  constructor(send: HeartbeatSend) {
    this.send = send;
  }

  /**
   * Answer a received app-level ping by SENDING the app-level pong synchronously
   * (echo `seq` verbatim). Called from the client's inbound-frame dispatch the
   * instant a `ping` frame is parsed — never deferred, never the protocol pong.
   */
  onPing(ping: InboundPingFrame): void {
    const pong: OutboundPongFrame = { type: "pong", seq: ping.seq };
    this.send(serializeFrame(pong));
    this.lastAnsweredSeq = ping.seq;
    this.pongCount += 1;
  }

  /** The last `seq` answered, or null if none yet (introspection / tests). */
  getLastAnsweredSeq(): number | null {
    return this.lastAnsweredSeq;
  }

  /** Total pongs sent on this responder (introspection / tests). */
  getPongCount(): number {
    return this.pongCount;
  }
}
